import type { TeslaBatteryHistory, TeslaConnectionState } from "@home-control/shared";
import { AuthenticationError } from "../../api";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api";

/** GET /tesla/history 404s until the endpoint ships; callers show a dedicated message (spec §5.3). */
export class TeslaHistoryUnavailableError extends Error {
  constructor() {
    super("Tesla battery history endpoint is not available yet.");
    this.name = "TeslaHistoryUnavailableError";
  }
}

/**
 * Browser navigation target that starts the Tesla OAuth dance (spec §2.3).
 * Plain navigation, no auth header — the server route is public.
 */
export function teslaConnectUrl(callbackUrl: string): string {
  return `${apiBaseUrl}/tesla/connect?callback_url=${encodeURIComponent(callbackUrl)}`;
}

/** Fallback connection status when /state omits `teslaConnection` (spec §2). */
export async function fetchTeslaStatus(token: string): Promise<TeslaConnectionState> {
  const response = await fetch(`${apiBaseUrl}/tesla/status`, {
    headers: { authorization: `Bearer ${token}` }
  });
  if (response.status === 401) {
    throw new AuthenticationError("Session expired");
  }
  if (!response.ok) {
    throw new Error("Tesla status read failed");
  }
  const body = (await response.json()) as { connection: TeslaConnectionState };
  return body.connection;
}

/** Battery drain history. Query params match apps/api readTeslaHistoryRequest. */
export async function fetchTeslaHistory(
  token: string,
  range: string,
  cursor?: string
): Promise<TeslaBatteryHistory> {
  const query = new URLSearchParams({ range, session_limit: "6" });
  if (cursor) query.set("cursor", cursor);
  const response = await fetch(`${apiBaseUrl}/tesla/history?${query.toString()}`, {
    headers: { authorization: `Bearer ${token}` }
  });
  if (response.status === 401) {
    throw new AuthenticationError("Session expired");
  }
  if (response.status === 404) {
    throw new TeslaHistoryUnavailableError();
  }
  if (!response.ok) {
    throw new Error("Battery history request failed.");
  }
  const body = (await response.json()) as { history: TeslaBatteryHistory };
  return body.history;
}
